import { ToggleButtonGroup } from "@mui/material";
import clsx from "clsx";
import { FC, useContext, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { ErrorsDisplayContainer } from "@ryde/controls";
import { StationRouteContext, useCrudEditContext } from "@ryde/redux";
import {
  dynamicFormTabIndexAF,
  errorsCrudContentAtomFamily,
  isInnerItemEditingAtom,
  IStationRouteDto,
  StationRouteTabsEnum,
  useHaveCustomPermissions,
  useIsMobile,
} from "@ryde/shared";

import { StationRouteEditTopInfoContainer } from "./station-route.edit.top-info";
import { StationRouteHeaderInfos } from "./station-route.header.infos";
import { StationRouteMap } from "./station-route.map";
import {
  StationRouteTabsEarsContainer,
  StationRouteTabsErrorContainer,
  StationRouteTabsToggleButton,
  StationRouteTabsWrapperStyled,
} from "./station-route.styled";
import { StationRouteTabsBottomBlocks } from "./station-route.tabs.bottom-blocks";
import { StationRouteTabsTabProperties } from "./station-route.tabs.tab-properties";
import { StationRouteTabsTabStations } from "./station-route.tabs.tab-stations";
import { StationRouteTabsTabStatistics } from "./station-route.tabs.tab-statistics";

export const StationRouteTabsWrapper: FC = () => {
  const {
    entity,
    mode,
    data: { isEdit },
  } = useCrudEditContext<IStationRouteDto>();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));
  const [tab, setTab] = useRecoilState(dynamicFormTabIndexAF(entity));
  const { rideOpenerId } = useContext(StationRouteContext);
  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);
  const {
    hasPermissionViewRide,
    hasPermissionViewCourse,
    readProductionPermission,
  } = useHaveCustomPermissions();
  const {
    formState: { errors, isValid },
  } = useFormContext<Partial<IStationRouteDto>>();

  // Show if user can view rides, courses, and/or productions.
  const hasUsageStatsPermission =
    !!hasPermissionViewRide ||
    !!hasPermissionViewCourse ||
    !!readProductionPermission;
  // Edit-only: create has no usage stats yet.
  const showStatisticsTab = !!isEdit && hasUsageStatsPermission;

  const isTabSwitchDisplayed = useMemo(() => {
    return isMobile || !rideOpenerId;
  }, [isMobile, rideOpenerId]);

  useEffect(() => {
    if (!isTabSwitchDisplayed) {
      setTab(StationRouteTabsEnum.Stations);
    }
  }, [isMobile, isTabSwitchDisplayed, setTab, tab]);

  // Leave Statistics if permission/edit mode no longer allows it.
  useEffect(() => {
    if (!showStatisticsTab && tab === StationRouteTabsEnum.Statistics) {
      setTab(StationRouteTabsEnum.Stations);
    }
  }, [setTab, showStatisticsTab, tab]);

  const handleTabSwitch = useEvent(
    (
      _: React.MouseEvent<HTMLElement>,
      nextView: StationRouteTabsEnum,
    ): void => {
      setTab(nextView);
    },
  );

  return (
    <StationRouteTabsWrapperStyled className="StationRouteDrawer-Form">
      <StationRouteEditTopInfoContainer />

      {isMobile && <StationRouteHeaderInfos />}

      {isTabSwitchDisplayed && (
        <StationRouteTabsEarsContainer
          className={clsx(isInnerItemEditing && "inner-item-editing")}
        >
          <ToggleButtonGroup
            color="primary"
            size="small"
            value={tab}
            exclusive
            onChange={handleTabSwitch}
            aria-label="Platform"
          >
            <StationRouteTabsToggleButton
              value={StationRouteTabsEnum.Stations}
              disabled={tab === StationRouteTabsEnum.Stations}
            >
              {t("selectListLabel.stations")}
            </StationRouteTabsToggleButton>
            <StationRouteTabsToggleButton
              value={StationRouteTabsEnum.Properties}
              disabled={tab === StationRouteTabsEnum.Properties}
            >
              {t("selectListLabel.properties")}
            </StationRouteTabsToggleButton>
            {showStatisticsTab && (
              <StationRouteTabsToggleButton
                value={StationRouteTabsEnum.Statistics}
                disabled={tab === StationRouteTabsEnum.Statistics}
              >
                {t("selectListLabel.statistics")}
              </StationRouteTabsToggleButton>
            )}
            {isMobile && (
              <StationRouteTabsToggleButton
                value={StationRouteTabsEnum.Map}
                disabled={tab === StationRouteTabsEnum.Map}
              >
                {t("selectListLabel.map")}
              </StationRouteTabsToggleButton>
            )}
          </ToggleButtonGroup>
        </StationRouteTabsEarsContainer>
      )}
      <StationRouteTabsErrorContainer>
        <ErrorsDisplayContainer
          formErrors={isValid ? undefined : errors}
          serverErrors={serverErrors}
        />
      </StationRouteTabsErrorContainer>
      {tab === StationRouteTabsEnum.Stations && <StationRouteTabsTabStations />}
      {tab === StationRouteTabsEnum.Properties && (
        <StationRouteTabsTabProperties />
      )}
      {/* Mount only when active so usage-stats is fetched lazily, not on form open. */}
      {showStatisticsTab && tab === StationRouteTabsEnum.Statistics && (
        <StationRouteTabsTabStatistics />
      )}
      {isMobile && tab === StationRouteTabsEnum.Map && (
        <StationRouteMap mode={mode} />
      )}
      <StationRouteTabsBottomBlocks />
    </StationRouteTabsWrapperStyled>
  );
};
